import { useEffect, useMemo, useState } from 'react';
import type { Discipline } from '@obraplan/core/domain/structure/discipline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';

/**
 * "Disciplinas que executa" — o que a proposta automática leva a equipe a fazer (Fase 30,
 * RN-431 a RN-433).
 *
 * Sem marcação, a equipe executa o que a função de **mais da metade** dos membros de hoje
 * executa. O diálogo abre com essas caixas marcadas **em estado de padrão** — o texto diz de
 * onde vieram, e salvar sem mexer não grava nada: gravar a maioria de hoje a congelaria, e ela
 * deixaria de acompanhar quem entra e sai.
 *
 * Mexer numa caixa transforma a lista em marcação da equipe. "Voltar ao padrão" manda a lista
 * vazia, e a equipe volta à maioria.
 *
 * **Só disciplinas ativas**, como na matriz função × disciplina: a marca de uma disciplina
 * desativada fica guardada — vai junto em toda gravação — e volta a aparecer se ela for
 * reativada.
 */
export interface TeamDisciplinesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamName: string;
  /** As disciplinas da obra (as globais e as dela). */
  disciplines: readonly Discipline[];
  /** As marcadas na equipe. Vazia: vale a maioria. */
  marked: readonly string[];
  /** O que mais da metade dos membros de hoje executa. */
  majority: readonly string[];
  canManage: boolean;
  isPending: boolean;
  errorMessage?: string;
  /** A lista inteira; vazia volta ao padrão. */
  onSubmit: (disciplineIds: readonly string[]) => void;
}

export function TeamDisciplinesDialog({
  open,
  onOpenChange,
  teamName,
  disciplines,
  marked,
  majority,
  canManage,
  isPending,
  errorMessage,
  onSubmit,
}: TeamDisciplinesDialogProps) {
  const active = useMemo(
    () =>
      disciplines
        .filter((discipline) => discipline.isActive)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
    [disciplines],
  );
  const activeIds = useMemo(() => new Set<string>(active.map((item) => item.id)), [active]);

  const isDefault = marked.length === 0;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [touched, setTouched] = useState(false);

  // Abrir parte do que vale hoje: a marcação, ou a maioria em estado de padrão.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(isDefault ? majority : marked));
    setTouched(false);
    // A lista viva pode chegar enquanto o diálogo está aberto; quem decide o ponto de partida
    // é a abertura, para a caixa recém-marcada não voltar atrás.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showingDefault = isDefault && !touched;

  const toggle = (id: string): void => {
    setTouched(true);
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = (): void => {
    // As marcas das desativadas vão junto: sem elas, salvar as apagaria caladas.
    const hidden = marked.filter((id) => !activeIds.has(id));
    onSubmit([...new Set([...selected, ...hidden])]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disciplinas que executa · {teamName}</DialogTitle>
          <DialogDescription>
            A proposta automática leva a equipe às frentes destas disciplinas. A jornada continua
            sendo de cada pessoa.
          </DialogDescription>
        </DialogHeader>

        {showingDefault && (
          <p className="flex flex-wrap items-center gap-2 text-sm text-(--color-content-muted)">
            <Badge tone="neutral">Padrão</Badge>
            {majority.length === 0
              ? 'Padrão: o que mais da metade da equipe executa — hoje, nenhuma disciplina. Marque as que a equipe executa.'
              : 'Padrão: o que mais da metade da equipe executa'}
          </p>
        )}

        {active.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            Nenhuma disciplina ativa nesta obra.
          </p>
        ) : (
          <ul
            className="grid max-h-[55vh] gap-x-4 overflow-y-auto sm:grid-cols-2"
            aria-label={`Disciplinas que ${teamName} executa`}
          >
            {active.map((discipline) => {
              const checked = selected.has(discipline.id);
              return (
                <li key={discipline.id} className="min-w-0">
                  <label
                    className={cn(
                      'flex h-11 min-w-0 items-center gap-2 text-sm md:h-9',
                      showingDefault && checked && 'italic text-(--color-content-muted)',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0"
                      checked={checked}
                      disabled={!canManage || isPending}
                      onChange={() => toggle(discipline.id)}
                    />
                    <span className="truncate" title={discipline.name}>
                      {discipline.name}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {errorMessage !== undefined && (
          <p role="alert" className="text-sm text-(--color-state-danger)">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          {canManage && !isDefault && (
            <Button
              type="button"
              variant="ghost"
              className="sm:mr-auto"
              disabled={isPending}
              onClick={() => onSubmit([])}
            >
              Voltar ao padrão
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {canManage ? 'Cancelar' : 'Fechar'}
          </Button>
          {canManage && (
            <Button type="button" variant="primary" disabled={isPending || !touched} onClick={save}>
              {isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
